import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts } from '../../../mocks/blog';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, inView };
}

export default function BlogSection() {
  const { ref, inView } = useInView();

  return (
    <section id="blog" className="py-28 bg-zinc-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div ref={ref} className={`flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 rounded-full px-4 py-1.5 mb-6">
              <i className="ri-article-line text-orange-500 text-sm" />
              <span className="text-orange-600 text-sm font-semibold">Blog & Aktuality</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-zinc-900 leading-tight">
              Novinky zo sveta
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500">
                digitálneho marketingu
              </span>
            </h2>
          </div>
          <p className="text-zinc-500 text-base max-w-xs md:text-right leading-relaxed md:pb-2">
            Praktické rady, case studies a trendy, ktoré vám pomôžu rásť online.
          </p>
        </div>

        {/* Blog grid */}
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-7 transition-all duration-700 delay-200 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {blogPosts.slice(0, 3).map((post, i) => (
            <BlogCard key={post.id} post={post} featured={i === 0} delay={i * 100} inView={inView} />
          ))}
        </div>

        {/* All posts CTA */}
        <div className={`text-center mt-12 transition-all duration-700 delay-500 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-zinc-700 hover:text-zinc-900 font-semibold text-sm border border-zinc-200 hover:border-zinc-400 bg-white px-6 py-3 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            Zobraziť všetky články
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}

interface BlogCardProps {
  post: typeof blogPosts[0];
  featured: boolean;
  delay: number;
  inView: boolean;
}

function BlogCard({ post, featured, delay, inView }: BlogCardProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <Link
      to={`/blog/${post.id}`}
      className={`group block bg-white rounded-2xl overflow-hidden border border-zinc-100 transition-all duration-500 cursor-pointer
        ${hovered ? '-translate-y-1 shadow-xl shadow-zinc-100' : ''}
        ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
      `}
      style={{ transitionDelay: `${delay}ms` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Image */}
      <div className="aspect-video overflow-hidden">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Category + read time */}
        <div className="flex items-center justify-between mb-4">
          <span className={`text-xs font-semibold rounded-full px-3 py-1 ${post.categoryColor}`}>
            {post.category}
          </span>
          <span className="text-zinc-400 text-xs flex items-center gap-1">
            <i className="ri-time-line text-xs" />
            {post.readTime}
          </span>
        </div>

        {/* Title */}
        <h3 className="text-zinc-900 font-bold text-lg mb-3 leading-snug group-hover:text-orange-600 transition-colors duration-200">
          {post.title}
        </h3>

        {/* Excerpt */}
        <p className="text-zinc-500 text-sm leading-relaxed mb-5 line-clamp-2">
          {post.excerpt}
        </p>

        {/* Author + date */}
        <div className="flex items-center justify-between pt-4 border-t border-zinc-50">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-400 to-amber-400 flex items-center justify-center">
              <span className="text-white text-xs font-bold">
                {post.author.charAt(0)}
              </span>
            </div>
            <span className="text-zinc-600 text-xs font-medium">{post.author}</span>
          </div>
          <span className="text-zinc-400 text-xs">{post.date}</span>
        </div>
      </div>
    </Link>
  );
}
